import { useEffect, useRef, useState } from 'react'
import { GoogleAuthService } from '../services/googleAuth'

interface UseGoogleAuthOptions {
  clientId: string
  enableOneTap?: boolean
  onSuccess?: () => void
  onError?: (error: Error) => void
}

export function useGoogleAuth({ 
  clientId, 
  enableOneTap = false,
  onSuccess,
  onError 
}: UseGoogleAuthOptions) {
  console.log('🔵 useGoogleAuth hook called with:', { clientId, enableOneTap })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const initialized = useRef(false)

  useEffect(() => {
    const initializeGoogle = async () => {
      console.log('🔵 useGoogleAuth initializeGoogle called')
      if (!clientId || initialized.current) {
        console.log('🟡 Skipping initialization - no clientId or already initialized')
        return
      }

      try {
        console.log('🔵 Starting Google Auth initialization...')
        setIsLoading(true)
        setError(null)

        // Set up authentication error callback to integrate with our hook
        GoogleAuthService.setAuthErrorCallback((error: Error) => {
          console.log('🔴 Auth error callback from GoogleAuthService:', error.message)
          setError(error.message)
          onError?.(error)
        })

        await GoogleAuthService.initialize(clientId)

        if (enableOneTap) {
          console.log('🔵 One Tap enabled, showing One Tap prompt')
          await GoogleAuthService.showOneTap(clientId)
        } else {
          console.log('🟡 One Tap disabled')
        }

        initialized.current = true
        console.log('🟢 useGoogleAuth initialization completed')
        // Don't call onSuccess here - it should only be called after actual authentication
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize Google Auth'
        console.error('🔴 useGoogleAuth initialization error:', err)
        setError(errorMessage)
        onError?.(new Error(errorMessage))
      } finally {
        setIsLoading(false)
      }
    }

    initializeGoogle()

    // Cleanup callback on unmount or dependency change
    return () => {
      if (initialized.current) {
        GoogleAuthService.setAuthErrorCallback(null)
      }
    }
  }, [clientId, enableOneTap, onSuccess, onError])

  const renderSignInButton = async (
    container: HTMLElement, 
    options?: {
      theme?: 'outline' | 'filled_blue' | 'filled_black'
      size?: 'large' | 'medium' | 'small'
      type?: 'standard' | 'icon'
      shape?: 'rectangular' | 'pill'
      text?: 'signin_with' | 'continue_with'
      width?: number
    }
  ) => {
    try {
      setError(null)
      await GoogleAuthService.renderSignInButton(container, clientId, options)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to render Google Sign-In button'
      setError(errorMessage)
      onError?.(new Error(errorMessage))
    }
  }

  const signOut = async () => {
    try {
      setError(null)
      await GoogleAuthService.signOut()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to sign out'
      setError(errorMessage)
      onError?.(new Error(errorMessage))
    }
  }

  return {
    isLoading,
    error,
    renderSignInButton,
    signOut,
    isInitialized: initialized.current
  }
}